import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useTenantStore } from '@/hooks/useTenantStore';

interface Schedule {
  id: string;
  frequency: string;
  recipients: string[];
  period_days: number;
  active: boolean;
  last_sent_at: string | null;
  next_send_at: string | null;
}

export default function LiquidityDeliverySchedule() {
  const { tenantId } = useTenantStore();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [frequency, setFrequency] = useState('MONTHLY');
  const [recipients, setRecipients] = useState('');
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    if (tenantId) {
      loadSchedules();
    }
  }, [tenantId]);

  async function loadSchedules() {
    if (!tenantId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('liquidity_delivery_schedules')
        .select('*')
        .eq('company_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSchedules(data || []);
    } catch (error: any) {
      console.error('Error loading schedules:', error);
      toast.error(error.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }

  async function saveSchedule() {
    if (!tenantId) {
      toast.error('Please select a company first');
      return;
    }

    if (!recipients.trim()) {
      toast.error('Please enter at least one recipient email');
      return;
    }

    const recipientList = recipients
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email);

    if (recipientList.length === 0) {
      toast.error('Please enter valid email addresses');
      return;
    }

    setLoading(true);
    try {
      const nextSendAt = calculateNextSendDate(frequency);

      const { error } = await supabase.from('liquidity_delivery_schedules').insert({
        company_id: tenantId,
        frequency,
        recipients: recipientList,
        period_days: periodDays,
        next_send_at: nextSendAt,
      });

      if (error) throw error;

      toast.success('Delivery schedule created successfully');
      setRecipients('');
      loadSchedules();
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      toast.error(error.message || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  }

  async function deleteSchedule(id: string) {
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('liquidity_delivery_schedules').delete().eq('id', id);

      if (error) throw error;

      toast.success('Schedule deleted successfully');
      loadSchedules();
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      toast.error(error.message || 'Failed to delete schedule');
    } finally {
      setLoading(false);
    }
  }

  async function toggleSchedule(id: string, active: boolean) {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('liquidity_delivery_schedules')
        .update({ active: !active })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Schedule ${!active ? 'activated' : 'deactivated'} successfully`);
      loadSchedules();
    } catch (error: any) {
      console.error('Error toggling schedule:', error);
      toast.error(error.message || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  }

  function calculateNextSendDate(freq: string): string {
    const now = new Date();
    switch (freq) {
      case 'WEEKLY':
        now.setDate(now.getDate() + 7);
        break;
      case 'QUARTERLY':
        now.setMonth(now.getMonth() + 3);
        break;
      case 'MONTHLY':
      default:
        now.setMonth(now.getMonth() + 1);
        break;
    }
    return now.toISOString();
  }

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Automated Liquidity Reports</CardTitle>
          <CardDescription>Please select a company to manage delivery schedules</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Automated Liquidity Reports</CardTitle>
        <CardDescription>
          Schedule automated delivery of liquidity reports to board members
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEEKLY">Weekly</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                  <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="period">Report Period (days)</Label>
              <Input
                id="period"
                type="number"
                value={periodDays}
                onChange={(e) => setPeriodDays(parseInt(e.target.value))}
                min={1}
                max={365}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipients">Recipients (comma-separated emails)</Label>
            <Input
              id="recipients"
              placeholder="john@example.com, jane@example.com"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </div>

          <Button onClick={saveSchedule} disabled={loading} className="w-full">
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Schedule
          </Button>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Active Schedules</h3>
          {schedules.length === 0 ? (
            <p className="text-muted-foreground">No schedules configured yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next Send</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>{schedule.frequency}</TableCell>
                    <TableCell>{schedule.recipients.length} recipient(s)</TableCell>
                    <TableCell>
                      <Badge variant={schedule.active ? 'default' : 'secondary'}>
                        {schedule.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {schedule.next_send_at
                        ? new Date(schedule.next_send_at).toLocaleDateString()
                        : 'Not scheduled'}
                    </TableCell>
                    <TableCell className="space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSchedule(schedule.id, schedule.active)}
                      >
                        {schedule.active ? 'Pause' : 'Activate'}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteSchedule(schedule.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
